// A link preview's picture, sealed by the sender (#87, D318–D320). The
// sender's device asks Twinden's server for the page's picture (fetched
// there once, never kept), shrinks it here and seals the small copy into
// the message or Links record. Readers show only that copy: no device ever
// loads a picture from the linked site, and older previews that only name
// one show without it.
import { LINK_PREVIEW_LIMITS } from '@twinden/shared';
import { api, type LinkPreview, type SealedPreviewImage } from './api';
import { fitWithin } from './photos';

const SHOWN_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;
// Base64 is 4/3 of the bytes; a little slack for padding.
const MAX_DATA_LENGTH =
  Math.ceil((LINK_PREVIEW_LIMITS.SEALED_IMAGE_MAX_BYTES * 4) / 3) + 4;

/** The picture as the server returns it: the site's bytes, unshrunk. */
export interface FetchedPreviewImage {
  mime: string;
  data: string;
}

export type ShrinkPreviewImage = (
  image: FetchedPreviewImage
) => Promise<SealedPreviewImage | null>;

/**
 * Where a preview's picture can be shown from: its sealed copy, as a data:
 * address, or nothing. Never `imageUrl`, the site's own address (D320).
 */
export function previewImageSrc(preview: {
  image?: unknown;
}): string | undefined {
  const image = preview.image as Partial<SealedPreviewImage> | undefined;
  if (!image || typeof image !== 'object') return undefined;
  if (typeof image.mime !== 'string' || !SHOWN_TYPES.has(image.mime)) {
    return undefined;
  }
  if (
    typeof image.data !== 'string' ||
    image.data.length > MAX_DATA_LENGTH ||
    !BASE64.test(image.data)
  ) {
    return undefined;
  }
  return `data:${image.mime};base64,${image.data}`;
}

/**
 * What goes into the envelope: the preview without the site's picture
 * address, and with the sealed copy when there is one (D318).
 */
export function sealablePreview(
  preview: LinkPreview,
  image: SealedPreviewImage | null
): LinkPreview {
  return {
    status: preview.status,
    url: preview.url,
    title: preview.title,
    description: preview.description,
    imageUrl: null,
    siteName: preview.siteName,
    ...(image ? { image } : {}),
  };
}

function base64Of(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * The sealed copy: at most SEALED_IMAGE_MAX_SIDE on its longest side, a
 * JPEG (on white, for transparent pictures) of at most
 * SEALED_IMAGE_MAX_BYTES, trying a lower quality before giving up. Re-encoding
 * also drops anything hidden in the site's file. Null: no picture.
 */
export const shrinkPreviewImage: ShrinkPreviewImage = async (fetched) => {
  if (!BASE64.test(fetched.data)) return null;
  const bytes = Uint8Array.from(atob(fetched.data), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: fetched.mime }));
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Unreadable image'));
      element.src = url;
    });
    const size = fitWithin(
      image.naturalWidth,
      image.naturalHeight,
      LINK_PREVIEW_LIMITS.SEALED_IMAGE_MAX_SIDE
    ) ?? { width: image.naturalWidth, height: image.naturalHeight };
    if (size.width <= 0 || size.height <= 0) return null;
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size.width, size.height);
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, size.width, size.height);
    for (const quality of LINK_PREVIEW_LIMITS.SEALED_IMAGE_QUALITIES) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality)
      );
      if (blob && blob.size <= LINK_PREVIEW_LIMITS.SEALED_IMAGE_MAX_BYTES) {
        return {
          mime: 'image/jpeg',
          data: base64Of(new Uint8Array(await blob.arrayBuffer())),
          width: size.width,
          height: size.height,
        };
      }
    }
    return null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
};

/**
 * A link's preview for a sealed message or Links record (D61, D318):
 * asked of Twinden's server (not kept there), its picture shrunk and
 * included, and the site's picture address left out. Null when there's no
 * usable preview, or it took longer than the send waits.
 */
export async function sealedPreviewFor(
  url: string,
  shrink: ShrinkPreviewImage = shrinkPreviewImage
): Promise<LinkPreview | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => {
        const response = await api.getLinkPreview(url, {
          ephemeral: true,
          image: true,
        });
        const preview = response.data as LinkPreview & {
          image?: FetchedPreviewImage;
        };
        if (preview.status !== 'ok') return null;
        const fetched = preview.image;
        const image =
          fetched && typeof fetched.data === 'string'
            ? await shrink(fetched).catch(() => null)
            : null;
        return sealablePreview(preview, image);
      })(),
      new Promise<null>((resolve) => {
        timer = setTimeout(
          () => resolve(null),
          LINK_PREVIEW_LIMITS.SEND_WAIT_MS
        );
      }),
    ]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
